import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getResourceById,
  createResource,
  updateResource,
  deleteResource,
  getResources,
  type ResourceFilters,
  ResourceUpdate,
} from '../../services';
import { toaster } from '../../components/ui/toaster';

export const resourceKeys = {
  all: ['resources'] as const,
  lists: () => [...resourceKeys.all, 'list'] as const,
  list: (filters: ResourceFilters) =>
    [...resourceKeys.lists(), { ...filters }] as const,
  details: () => [...resourceKeys.all, 'detail'] as const,
  detail: (id: number) => [...resourceKeys.details(), id] as const,
};

export function useResources(filters: ResourceFilters) {
  return useQuery({
    queryKey: resourceKeys.list(filters),
    queryFn: () => getResources(filters),
    placeholderData: (previousData) => previousData,
  });
}

export function useResourceById(id: number) {
  return useQuery({
    queryKey: resourceKeys.detail(id),
    queryFn: () => getResourceById(id),
    enabled: !!id,
  });
}

export function useCreateResource() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createResource,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: resourceKeys.lists() });
      toaster.create({
        title: 'Resource created',
        type: 'success',
        duration: 3000,
      });
    },
    onError: (err: unknown) => {
      toaster.create({
        title: 'Error',
        description:
          err instanceof Error ? err.message : 'Something went wrong',
        type: 'error',
        duration: 4000,
      });
    },
  });
}

export function useUpdateResource() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: ResourceUpdate) => updateResource(id, data),
    onSuccess: (_, { id }) => {
      toaster.create({
        title: 'Resource updated',
        type: 'success',
        duration: 3000,
      });
      queryClient.invalidateQueries({ queryKey: resourceKeys.lists() });
      queryClient.invalidateQueries({ queryKey: resourceKeys.detail(id) });
    },
    onError: (err: unknown) => {
      toaster.create({
        title: 'Error',
        description:
          err instanceof Error ? err.message : 'Something went wrong',
        type: 'error',
        duration: 4000,
      });
    },
  });
}

export function useDeleteResource() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteResource,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: resourceKeys.lists() });
      toaster.create({
        title: 'Resource deleted',
        type: 'success',
        duration: 3000,
      });
    },
    onError: (err: unknown) => {
      toaster.create({
        title: 'Error',
        description:
          err instanceof Error ? err.message : 'Something went wrong',
        type: 'error',
        duration: 3000,
      });
    },
  });
}
